import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Tag, TagType } from '@anyx/feature-crm/shared';
import { AnychatFaIcon, AnyTagIcon } from '@anyx/shared-icons';

const Status = ({ synced }: { synced?: boolean }) => {
  const { t } = useTranslation('crm');

  return synced ? (
    <Tag
      className="ml-2"
      classNames={{ text: 'text-gray-9', buttonWrapper: 'bg-green-1 rounded-full' }}
      size="xs"
      type="default"
      label={t('crm.tags.syncStatus.synced', { ns: 'crm' })}
    />
  ) : (
    <Tag
      className="ml-2"
      classNames={{ text: 'text-gray-6', buttonWrapper: 'bg-gray-2 rounded-full' }}
      size="xs"
      type="default"
      label={t('crm.tags.syncStatus.notSynced', {
        ns: 'crm',
      })}
    />
  );
};

const getBaseProps = ({ type = 'default' }: { type: TagType }) => {
  return {
    anytag: {
      title: (
        <div className="text-2xs flex items-center gap-1">
          <AnyTagIcon className="h-3 w-3" />
          <span className="text-gray-7">AnyTag</span>
        </div>
      ),
    },
    anychat: {
      title: (
        <div className="text-2xs flex items-center gap-1">
          <AnychatFaIcon className="h-3 w-3" />
          <span className="text-gray-7">Anychat</span>
        </div>
      ),
    },
    default: {
      title: '',
    },
  }[type];
};

export type TagItem = string | { id: string; name: string };

export interface TagDisplayProps {
  type: TagType;
  tags: TagItem[];
  aside?: ReactNode;
  showSyncStatus?: boolean;
  synced?: boolean;
  disabled?: boolean;
  onRemove?: (id?: string) => void;
}

export const TagDisplay = ({
  type = 'default',
  tags,
  aside,
  synced,
  showSyncStatus = true,
  disabled,
  onRemove,
}: TagDisplayProps) => {
  const { title } = getBaseProps({ type });

  const normalizeTag = (tag: TagItem) => {
    return typeof tag === 'string' ? { id: undefined, name: tag } : { id: tag.id, name: tag.name };
  };

  return (
    <section>
      {(title || aside) && (
        <header className="mb-2 flex items-center justify-between">
          <div className="flex items-center">
            {title} {showSyncStatus && <Status synced={synced} />}
          </div>
          {aside}
        </header>
      )}
      {tags.length > 0 && (
        <div className="break-anywhere border-gray-1 relative flex max-h-24 flex-wrap gap-x-1 gap-y-1.5 overflow-x-clip overflow-y-auto rounded-sm border p-2">
          {tags.map((tag) => {
            const { id, name } = normalizeTag(tag);
            return (
              <Tag
                key={id ? `${type}-${id}` : `${type}-${name}`}
                id={id}
                type={type}
                size="sm"
                hideCount
                label={name}
                disabled={disabled}
                onRemove={onRemove}
              />
            );
          })}
        </div>
      )}
    </section>
  );
};
